/**
 * `<OverflowRow>` — рядок з «Ще · N» на `useOverflowRow` (components.md, розд. 12): для чипів подань,
 * дій панелі й нижньої навігації. Кожен елемент — у своїй обгортці `.ovf__item` (її міряє хук), «Ще» —
 * меню з пунктами схованих елементів і власними пунктами `extra` (`overflow-menu.ts`, у лінивому
 * модулі шарів: пункти потрібні лише відкритому меню). Обгортки й «Ще» — один масив з
 * ключами: перехід у «Ще» й назад не перемонтовує елемента. Рядки з власною семантикою
 * (вкладки картки — `tablist`, чипи «Стрічки», теги) беруть хук напряму, без цього компонента.
 *
 * Підпис, що не ширшає від жирного, — `<StableText>` (`StableText.tsx`).
 */
import { createContext, type ReactNode, useContext, useLayoutEffect, useRef } from 'react';
import type { MenuItem, OverflowEntry } from './layer-types.ts';
import { MenuButton } from './Menu.tsx';
import { useLayerModule } from './Popover.tsx';
import { useOverflowRow } from './useOverflowRow.ts';

export type { OverflowEntry };

export interface OverflowRowProps {
  items: readonly OverflowEntry[];
  selected?: string | null | undefined;
  /** Завжди в рядку: основна дія («Створити …»). */
  pinned?: readonly string[] | undefined;
  /** Порядок, у якому елементи займають місце; немає — порядок рядка (в «Ще» першими йдуть останні). */
  priority?: readonly string[] | undefined;
  /** Власні пункти «Ще» після схованих: кнопка тоді є завжди. */
  extra?: readonly MenuItem[] | undefined;
  className?: string | undefined;
  /** Контейнер — елемент flex між сусідами: стала основа й мінімум (див. `useOverflowRow`). */
  flex?: boolean | undefined;
  /** `nav` — орієнтир навігації; типово `div`. */
  as?: 'div' | 'nav';
  /** Роль групи (`group`) з підписом — для рядка перемикачів. */
  role?: 'group' | undefined;
  label?: string | undefined;
  id?: string | undefined;
  /** `-1` — ціль переходу «До навігації» (skip-links), не зупинка `Tab`. */
  tabIndex?: number | undefined;
  testId?: string | undefined;
  /** Підпис кнопки «Ще» для програми читання: що в ній. */
  moreLabel: (names: readonly string[]) => string;
  /** Вміст кнопки «Ще» за кількістю схованих. */
  more: (count: number) => ReactNode;
  moreClassName: string;
  moreAlign?: 'start' | 'end';
}

/** Чи комірка, де живе компонент, зараз у «Ще» (поза рядком). */
const CellOut = createContext(false);

/**
 * Елемент рядка дізнається, що його сховано в «Ще»: власний шар (результати пошуку, підказки) тоді
 * закривається — людина не бачить поля, від якого він відкритий.
 */
export function useInOverflow(): boolean {
  return useContext(CellOut);
}

export function OverflowRow(props: OverflowRowProps) {
  const { items, extra = [], as: Tag = 'div' } = props;
  const row = useOverflowRow<HTMLDivElement>({
    keys: items.map((x) => x.key),
    selected: props.selected,
    pinned: props.pinned,
    priority: props.priority,
    always: extra.length > 0,
  });
  const byKey = new Map(items.map((x) => [x.key, x]));

  // Пункти «Ще» потрібні лише відкритому меню, а воно малюється з лінивим модулем шарів.
  const layers = useLayerModule(false);

  // Вибір з «Ще» елемента з `refocus`: фокус на ньому, щойно він став у рядок (`focusChosen`). Ключ
  // ставить пункт «Ще» (`overflowMenu`), тож модуль шарів тоді вже є.
  const focusKey = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (focusKey.current !== null) layers?.focusChosen(focusKey, row.ref.current, row.out);
  });

  const hidden = row.hidden.flatMap((key) => byKey.get(key) ?? []);
  const names = hidden.map((entry) => entry.name);
  const menu = layers
    ? layers.overflowMenu(hidden, extra, (key) => {
        focusKey.current = key;
      })
    : [];
  const className = ['ovf', props.className].filter(Boolean).join(' ');
  // «Ще» — перед закріпленими в кінці рядка: основна дія лишається крайньою.
  const pinnedSet = new Set(props.pinned ?? []);
  let cut = row.row.length;
  while (cut > 0 && pinnedSet.has(row.row[cut - 1] ?? '')) cut -= 1;
  // Один масив з ключами в порядку `row.dom`: елемент, що йде в «Ще» й назад, лишається тим самим
  // вузлом (стан поля, відкрите меню, нагляд `ResizeObserver`), а не перемонтовується.
  const moreAt = cut < row.row.length ? row.dom.indexOf(row.row[cut] ?? '') : row.dom.length;
  const cells: ReactNode[] = row.dom.map((key) => (
    // Поза рядком — `visibility: hidden` (поза деревом доступності й Tab), ширина лишається.
    <span key={`i:${key}`} className="ovf__item" {...row.item(key)}>
      <CellOut.Provider value={row.out(key)}>{byKey.get(key)?.node}</CellOut.Provider>
    </span>
  ));
  cells.splice(
    moreAt < 0 ? cells.length : moreAt,
    0,
    <span key="more" className="ovf__more" {...row.more}>
      <MenuButton
        label={props.moreLabel(names)}
        items={menu}
        align={props.moreAlign ?? 'end'}
        className={props.moreClassName}
        tabIndex={row.moreOn ? undefined : -1}
      >
        {props.more(row.hidden.length)}
      </MenuButton>
    </span>,
  );

  return (
    <Tag
      ref={row.ref}
      className={className}
      role={props.role}
      aria-label={props.label}
      id={props.id}
      tabIndex={props.tabIndex}
      data-testid={props.testId}
      style={props.flex ? row.style : undefined}
    >
      {cells}
    </Tag>
  );
}
